import React, { createContext, useContext, useState, useEffect, useCallback, ReactNode } from "react";
import type {
  LinkedInJob, TechManager, JobStatus, AlertHistoryEntry, EmployeeSlim,
  ArchivedCleanupPreview, OldJobsCleanupPreview,
} from "../api/LinkedInJobsApi";
import {
  fetchAlerts, fetchAlertHistory, updateAlertStatus, reassignAlert, fetchTechManagers,
  fetchAllEmployeesSlim, fetchArchivedCleanupPreview, executeArchivedCleanup,
  fetchOldJobsPreview, executeOldJobsCleanup, fetchAlertStatusCounts,
} from "../api/LinkedInJobsApi";

export type RemoteScopeFilter = "worldwide" | "unclear" | "all";

export type CleanupModalState =
  | { type: "archived"; preview: ArchivedCleanupPreview }
  | { type: "old_jobs"; months: 6 | 12; preview: OldJobsCleanupPreview; cutoff_iso: string }
  | null;

export interface LinkedInJobsContextType {
  isAdmin: boolean;

  // Data
  jobs: LinkedInJob[];
  totalCount: number;
  totalPages: number;
  page: number;
  loading: boolean;
  loadError: string | null;

  // Filters
  search: string;
  searchInput: string;
  employmentType: string;
  seniority: string;

  // Filters — My Alerts tab
  remoteScopeFilter: RemoteScopeFilter;
  statusFilter: string;
  selectedManagerId: number | null;
  companyCanApplyOnly: boolean;
  techManagers: TechManager[];
  allEmployees: EmployeeSlim[];

  // Status counts for My Alerts chips
  statusCounts: Record<string, number>;

  // Selection + status update
  selectedJob: LinkedInJob | null;
  alertHistory: AlertHistoryEntry[];
  historyLoading: boolean;

  // Cleanup
  archivedPreview: ArchivedCleanupPreview | null;
  archivedPreviewLoading: boolean;
  cleanupModal: CleanupModalState;
  cleanupExecuting: boolean;
  cleanupError: string | null;

  // Actions
  loadJobs: (pg?: number) => Promise<void>;
  handleSearchSubmit: (e: React.FormEvent) => void;
  handleJobToggled: (updated: LinkedInJob) => void;
  clearFilters: () => void;
  setSearchInput: (v: string) => void;
  setEmploymentType: (v: string) => void;
  setSeniority: (v: string) => void;
  setSelectedJob: (job: LinkedInJob | null) => void;
  setRemoteScopeFilter: (v: RemoteScopeFilter) => void;
  setStatusFilter: (v: string) => void;
  setSelectedManagerId: (v: number | null) => void;
  setCompanyCanApplyOnly: (v: boolean) => void;
  handleStatusUpdate: (alertId: number, status: JobStatus, notes: string | null) => Promise<void>;
  handleReassignAlert: (alertId: number, employeeId: number) => Promise<void>;
  loadAlertHistory: (alertId: number) => Promise<void>;
  openArchivedCleanup: () => Promise<void>;
  openOldJobsCleanup: (months: 6 | 12) => Promise<void>;
  executeCleanup: () => Promise<void>;
  closeCleanupModal: () => void;
}

const LinkedInJobsContext = createContext<LinkedInJobsContextType | undefined>(undefined);

export function useLinkedInJobs(): LinkedInJobsContextType {
  const ctx = useContext(LinkedInJobsContext);
  if (!ctx) throw new Error("useLinkedInJobs must be used inside LinkedInJobsProvider");
  return ctx;
}

interface Props {
  isAdmin: boolean;
  children: ReactNode;
}

export function LinkedInJobsProvider({ isAdmin, children }: Props) {
  const [jobs, setJobs] = useState<LinkedInJob[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);

  // Filters
  const [search, setSearch] = useState("");
  const [searchInput, setSearchInput] = useState("");
  const [employmentType, setEmploymentType] = useState("");
  const [seniority, setSeniority] = useState("");

  // My Alerts filters
  const [remoteScopeFilter, setRemoteScopeFilter] = useState<RemoteScopeFilter>("all");
  const [statusFilter, setStatusFilter] = useState("new");
  const [selectedManagerId, setSelectedManagerId] = useState<number | null>(null);
  const [companyCanApplyOnly, setCompanyCanApplyOnly] = useState(false);
  const [techManagers, setTechManagers] = useState<TechManager[]>([]);
  const [allEmployees, setAllEmployees] = useState<EmployeeSlim[]>([]);

  // Status counts
  const [statusCounts, setStatusCounts] = useState<Record<string, number>>({});

  // Selection + history
  const [selectedJob, setSelectedJob] = useState<LinkedInJob | null>(null);
  const [alertHistory, setAlertHistory] = useState<AlertHistoryEntry[]>([]);
  const [historyLoading, setHistoryLoading] = useState(false);

  // Cleanup state
  const [archivedPreview, setArchivedPreview] = useState<ArchivedCleanupPreview | null>(null);
  const [archivedPreviewLoading, setArchivedPreviewLoading] = useState(false);
  const [cleanupModal, setCleanupModal] = useState<CleanupModalState>(null);
  const [cleanupExecuting, setCleanupExecuting] = useState(false);
  const [cleanupError, setCleanupError] = useState<string | null>(null);

  useEffect(() => {
    if (isAdmin) {
      fetchTechManagers().then(setTechManagers).catch((err) => {
        console.error("Failed to load technical managers:", err);
      });
      fetchAllEmployeesSlim().then(setAllEmployees).catch((err) => {
        console.error("Failed to load employees:", err);
      });
    }
  }, [isAdmin]);

  // Load status counts (or when manager filter changes)
  useEffect(() => {
    fetchAlertStatusCounts(isAdmin, isAdmin && selectedManagerId ? selectedManagerId : undefined)
      .then(setStatusCounts)
      .catch(() => setStatusCounts({}));
  }, [isAdmin, selectedManagerId]);

  // Auto-load archived preview for admin
  useEffect(() => {
    if (isAdmin) {
      setArchivedPreviewLoading(true);
      fetchArchivedCleanupPreview()
        .then(setArchivedPreview)
        .catch(() => setArchivedPreview(null))
        .finally(() => setArchivedPreviewLoading(false));
    }
  }, [isAdmin]);

  const loadJobs = useCallback(async (pg = 1) => {
    setLoading(true);
    setLoadError(null);
    // A page-1 load means the view itself changed (filters, tab switch, initial
    // mount) — clear stale results so the loading state below shows a spinner
    // instead of briefly displaying the previous view's jobs. Pagination within
    // the same view (page 2+) intentionally keeps the list visible while it refetches.
    if (pg === 1) setJobs([]);
    try {
      const remote_scope_filter = remoteScopeFilter !== "all" ? remoteScopeFilter : undefined;
      const result = await fetchAlerts(
        {
          page: pg,
          page_size: 20,
          status_filter: statusFilter || undefined,
          hide_archived: statusFilter !== "archived",
          employment_type: employmentType || undefined,
          seniority: seniority || undefined,
          search: search || undefined,
          remote_scope_filter,
          employee_id: isAdmin && selectedManagerId ? selectedManagerId : undefined,
          company_can_apply_only: companyCanApplyOnly || undefined,
        },
        isAdmin,
      );
      setJobs(result.items);
      setTotalCount(result.total_count);
      setTotalPages(result.total_pages);
      setPage(pg);
    } catch {
      setLoadError("Failed to load jobs. Please refresh and try again.");
    } finally {
      setLoading(false);
    }
  }, [
    search, employmentType, seniority, isAdmin,
    remoteScopeFilter, statusFilter, selectedManagerId, companyCanApplyOnly,
  ]);

  function clearFilters() {
    setSearch("");
    setSearchInput("");
    setEmploymentType("");
    setSeniority("");
    setRemoteScopeFilter("all");
    setStatusFilter("new");
    setSelectedManagerId(null);
    setCompanyCanApplyOnly(false);
    setPage(1);
  }

  function handleSearchSubmit(e: React.FormEvent) {
    e.preventDefault();
    setSearch(searchInput);
  }

  function handleJobToggled(updated: LinkedInJob) {
    setJobs((prev) => prev.map((j) => (j.id === updated.id ? updated : j)));
    setSelectedJob(updated);
  }

  async function handleStatusUpdate(alertId: number, status: JobStatus, notes: string | null) {
    await updateAlertStatus(alertId, status, notes, isAdmin);
    setJobs((prev) => prev.map((j) => j.alert_id === alertId ? { ...j, status, alert_notes: notes } : j));
    setSelectedJob((prev) =>
      prev && prev.alert_id === alertId ? { ...prev, status, alert_notes: notes } : prev
    );
    try { await loadAlertHistory(alertId); } catch {}
    fetchAlertStatusCounts(isAdmin, isAdmin && selectedManagerId ? selectedManagerId : undefined)
      .then(setStatusCounts).catch(() => {});
  }

  async function handleReassignAlert(alertId: number, employeeId: number) {
    const updated = await reassignAlert(alertId, employeeId);
    setJobs((prev) => prev.map((j) => j.alert_id === alertId ? { ...j, employee_id: updated.employee_id } : j));
    setSelectedJob((prev) =>
      prev && prev.alert_id === alertId ? { ...prev, employee_id: updated.employee_id } : prev
    );
  }

  async function loadAlertHistory(alertId: number) {
    setHistoryLoading(true);
    try {
      const history = await fetchAlertHistory(alertId, isAdmin);
      setAlertHistory(history);
    } catch {
      setAlertHistory([]);
    } finally {
      setHistoryLoading(false);
    }
  }

  async function openArchivedCleanup() {
    // Always re-fetch on every click — the tab-load snapshot may be stale (another session
    // could have archived more jobs since we loaded the count).
    setCleanupError(null);
    setArchivedPreviewLoading(true);
    try {
      const preview = await fetchArchivedCleanupPreview();
      setArchivedPreview(preview);
      setCleanupModal({ type: "archived", preview });
    } catch {
      setCleanupError("Failed to load cleanup preview. Please try again.");
    } finally {
      setArchivedPreviewLoading(false);
    }
  }

  async function openOldJobsCleanup(months: 6 | 12) {
    setCleanupError(null);
    try {
      const preview = await fetchOldJobsPreview(months);
      // Store cutoff_iso from the preview so execute uses the exact same timestamp boundary.
      setCleanupModal({ type: "old_jobs", months, preview, cutoff_iso: preview.cutoff_iso });
    } catch {
      setCleanupError("Failed to load preview. Please try again.");
    }
  }

  async function executeCleanup() {
    if (!cleanupModal) return;
    setCleanupExecuting(true);
    setCleanupError(null);
    try {
      if (cleanupModal.type === "archived") {
        await executeArchivedCleanup();
      } else {
        await executeOldJobsCleanup(cleanupModal.months, cleanupModal.cutoff_iso);
      }
      // Refresh archived preview for both cleanup types — old_jobs cleanup may have
      // deleted alerts that were archived, leaving a phantom count in the badge.
      const updated = await fetchArchivedCleanupPreview().catch(() => null);
      if (updated) setArchivedPreview(updated);
      setCleanupModal(null);
      await loadJobs(1);
    } catch {
      setCleanupError("Cleanup failed. Please try again.");
    } finally {
      setCleanupExecuting(false);
    }
  }

  function closeCleanupModal() {
    setCleanupModal(null);
    setCleanupError(null);
  }

  return (
    <LinkedInJobsContext.Provider value={{
      isAdmin,
      jobs, totalCount, totalPages, page, loading, loadError,
      search, searchInput, employmentType, seniority,
      remoteScopeFilter, statusFilter, selectedManagerId, companyCanApplyOnly, techManagers, allEmployees,
      statusCounts,
      selectedJob, alertHistory, historyLoading,
      archivedPreview, archivedPreviewLoading, cleanupModal, cleanupExecuting, cleanupError,
      loadJobs, handleSearchSubmit, handleJobToggled, clearFilters,
      setSearchInput, setEmploymentType, setSeniority, setSelectedJob,
      setRemoteScopeFilter, setStatusFilter, setSelectedManagerId, setCompanyCanApplyOnly,
      handleStatusUpdate, handleReassignAlert, loadAlertHistory,
      openArchivedCleanup, openOldJobsCleanup, executeCleanup, closeCleanupModal,
    }}>
      {children}
    </LinkedInJobsContext.Provider>
  );
}
